/**
 * draftPlayAdapter — lets the editor play its own unpublished draft.
 *
 * Preview used to mean "play the published Situation/Choice documents", which
 * under the draft model is exactly the wrong thing: those are the LAST published
 * version, so an author previewing their edits would be shown the old scenario.
 *
 * Rather than build a server-side preview of the draft (a second publish
 * pipeline, with its own ways to drift), this reshapes the draft graph the
 * editor already holds in memory into the same objects
 * `GET /api/situations/:situationId` returns. `ViewSituation` takes a
 * `situationSource` function; the play path passes the real service and the
 * editor passes this. Nothing downstream — SituationBody, ChoicesBody,
 * GroupBody, FreeResponseBody, UserSubmittedBody — can tell the difference, and
 * the published play path is untouched.
 *
 * The shape being matched (see server/controllers/SituationController.js):
 *   situation.choices[]           — the outgoing edges, as Choice documents
 *   choice.nextSituation          — POPULATED to { _id, title }
 *   choice.situation              — a bare id string
 *   situation.situationGroup      — POPULATED to the group document
 */

/** node.data holds every authoring field; isStart/isEnd derive from endpointType. */
function nodeToSituation(node, { choices = [], group = null } = {}) {
    const data = node.data || {};

    return {
        _id: node.id,
        title: data.title ?? '',
        text: data.text ?? '',
        image: data.image ?? null,
        audio: data.audio ?? null,
        audioLabel: data.audioLabel ?? '',
        document: data.document ?? null,
        autoplay: !!data.autoplay,
        isStart: data.endpointType === 'start',
        isEnd: data.endpointType === 'end',
        isFreeResponse: !!data.isFreeResponse,
        isUserSubmitted: !!data.isUserSubmitted,
        isLogged: !!data.isLogged,
        grantsKeys: data.grantsKeys || [],
        removesKeys: data.removesKeys || [],
        situationGroup: group,
        choices,
    };
}

/**
 * An outgoing edge, as the Choice document the play components expect.
 * `hasHint` is derived from the self-loop, exactly as publish derives it.
 */
function edgeToChoice(edge, { targetNode = null } = {}) {
    const data = edge.data || {};

    return {
        _id: edge.id,
        title: data.title ?? '',
        text: data.text ?? '',
        image: data.image ?? null,
        hint: data.hint ?? '',
        hasHint: edge.source === edge.target,
        correctness: data.correctness ?? null,
        isMLFRQ: !!data.isMLFRQ,
        mlfrqPairs: data.mlfrqPairs || [],
        key: data.key ?? null,
        hideIfKey: !!data.hideIfKey,
        showIfKey: !!data.showIfKey,
        grantsKeys: data.grantsKeys || [],
        removesKeys: data.removesKeys || [],

        // `situation` is a bare id and `nextSituation` is populated — ChoicesBody
        // compares `choice.situation` against `choice.nextSituation._id` to spot a
        // hint, so these two must keep their different shapes.
        situation: edge.source,
        nextSituation: targetNode
            ? { _id: targetNode.id, title: targetNode.data?.title ?? '' }
            : null,
    };
}

/**
 * Builds a `situationSource` bound to the current draft.
 *
 * @param {{nodes: Array, edges: Array, groups?: Array}} draft
 * @returns {(situationId: string) => Promise<Object|null>} Same contract as
 *   `SituationServices.getSituation`, so it drops straight into ViewSituation.
 */
export function createDraftSituationSource({ nodes = [], edges = [], groups = [] } = {}) {
    const nodesById = new Map(nodes.map((node) => [node.id, node]));
    // Draft groups are keyed by `id`; a published SituationGroup document by `_id`.
    // Preview always passes the draft, but keying on both means this cannot break
    // if a caller ever hands it the published documents instead.
    const groupsById = new Map(
        groups.map((group) => [String(group.id ?? group._id), group])
    );

    const outgoing = new Map();
    for (const edge of edges) {
        if (!outgoing.has(edge.source)) outgoing.set(edge.source, []);
        outgoing.get(edge.source).push(edge);
    }

    return async (situationId) => {
        const node = nodesById.get(situationId);
        if (!node) return null;

        const choices = (outgoing.get(situationId) || []).map((edge) =>
            edgeToChoice(edge, { targetNode: nodesById.get(edge.target) || null })
        );

        const groupId = node.data?.situationGroup;
        return nodeToSituation(node, {
            choices,
            group: groupId ? groupsById.get(String(groupId)) || null : null,
        });
    };
}

/** The draft's start node — where a preview begins. */
export function findDraftStartId(nodes = []) {
    return nodes.find((node) => node.data?.endpointType === 'start')?.id || null;
}

/**
 * The minimap shape `ViewCanvas` expects, taken from the draft rather than from
 * the published snapshot, so preview shows the graph being edited.
 */
export function toDraftCanvas({ nodes = [], edges = [] } = {}) {
    return { nodes, edges };
}

export { nodeToSituation, edgeToChoice };
